/**
 * animation.js - Animation Loop (Rotation, Breathing, Vertices)
 * Original CPU-basierte Vertex-Animation
 */
import { settings, DAMPING, AMOUNT } from "./settings.js";
import { scene, camera, renderer, clock } from "./scene.js";
import {
  currentMesh,
  positionClone,
  normalClone,
  countVertices,
} from "./mesh.js";
import { mapY, rotationImpulse, decayRotationImpulse } from "./input.js";
import { updateHair, syncHairRotation } from "./hair.js";
import { updateDissolve } from "./dissolve.js";
import { updateParticleMorph, getParticleSystem } from "./particleMorph.js";
import { updateWordDisplay } from "./wordDisplay.js";

// Für Delta-Zeit Berechnung
let lastTime = 0;

/**
 * Haupt-Animation Loop
 */
export function tick() {
  const now = Date.now() / 300;
  const currentTime = Date.now();
  const deltaTime = (currentTime - lastTime) / 1000; // in Sekunden
  lastTime = currentTime;

  // Word-Display Update
  if (settings.wordDisplay) {
    updateWordDisplay(clock.getElapsedTime(), deltaTime);
  }

  // Partikel-Morph Update
  if (settings.particleMorph) {
    updateParticleMorph(clock.getElapsedTime(), deltaTime);
  }

  // Donut-Modus (wenn particleMorph und wordDisplay deaktiviert)
  if (currentMesh && !settings.particleMorph && !settings.wordDisplay) {
    // Rotation auf currentMesh
    if (settings.rotation) {
      currentMesh.rotation.y += rotationImpulse;
      decayRotationImpulse();

      const deltaTime = clock.getElapsedTime() - settings.lastRotationUpdateTime;
      currentMesh.rotation.y += settings.rotationSpeed * deltaTime;
      settings.lastRotationUpdateTime = clock.getElapsedTime();
    }
  } else if (!settings.particleMorph && !settings.wordDisplay) {
    // Rotation Impulse decay auch wenn kein Mesh
    decayRotationImpulse();
  }

  if (currentMesh && !settings.particleMorph && !settings.wordDisplay) {

    // Breathing Animation
    if (settings.breathing) {
      const currentTime = clock.getElapsedTime();
      settings.breathingActiveTime += currentTime - settings.lastBreathingUpdateTime;
      settings.lastBreathingUpdateTime = currentTime;

      const geometry = currentMesh.geometry;

      for (let i = 0; i < countVertices; i++) {
        const ix = i * 3;
        const iy = i * 3 + 1;
        const iz = i * 3 + 2;

        const xPhase = Math.sin(settings.breathingActiveTime + positionClone[ix]) * settings.breathingIntensity;
        const yPhase = Math.sin(settings.breathingActiveTime + positionClone[iy]) * settings.breathingIntensity;
        const zPhase = Math.sin(settings.breathingActiveTime + positionClone[iz]) * settings.breathingIntensity;

        geometry.attributes.position.array[ix] = positionClone[ix] + normalClone[ix] * xPhase;
        geometry.attributes.position.array[iy] = positionClone[iy] + normalClone[iy] * yPhase;
        geometry.attributes.position.array[iz] = positionClone[iz] + normalClone[iz] * zPhase;
      }

      geometry.attributes.position.needsUpdate = true;
    }

    // Vertices Wave Animation
    if (settings.vertices && settings.mouseDeform) {
      if (settings.mouseIntensity) {
        settings.verticesIntensity = mapY;
      }

      const geometry = currentMesh.geometry;

      for (let i = 0; i < countVertices; i++) {
        const uX = geometry.attributes.uv.getX(i) * Math.PI * AMOUNT;
        const uY = geometry.attributes.uv.getY(i) * Math.PI * AMOUNT;

        const xsin = Math.sin(uX + now) * DAMPING;
        const ycos = Math.cos(uY + now) * DAMPING;

        const ix = i * 3;
        const iy = i * 3 + 1;
        const iz = i * 3 + 2;

        geometry.attributes.position.setX(
          i,
          positionClone[ix] + normalClone[ix] * (xsin + ycos) * 0.1 * settings.verticesIntensity
        );
        geometry.attributes.position.setY(
          i,
          positionClone[iy] + normalClone[iy] * (xsin + ycos) * 0.1 * settings.verticesIntensity
        );
        geometry.attributes.position.setZ(
          i,
          positionClone[iz] + normalClone[iz] * (xsin + ycos) * 0.1 * settings.verticesIntensity
        );
      }
    }

    // Normals und Position aktualisieren
    currentMesh.geometry.computeVertexNormals();
    currentMesh.geometry.attributes.position.needsUpdate = true;

    // Haar-Effekt (optional)
    if (settings.hair) {
      updateHair(now, mapY, currentMesh, deltaTime);
      syncHairRotation(currentMesh);
    }

    // Dissolve-Effekt (immer updaten wenn aktiv)
    updateDissolve(now, deltaTime, currentMesh);
  }

  // Render
  renderer.render(scene, camera);
  requestAnimationFrame(tick);
}
